/**
 * Booking API client for Stage Door Access
 * Handles booking creation, payment processing, and checkout flow
 */

import type { SessionIdentifier } from './events';
import { handleUnauthorized } from './auth';
import { throwApiError } from './client-error';
import { getAttribution } from '../lib/attribution';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

// =============================================================================
// Types
// =============================================================================

export interface BookingClassInput {
  classOptionId: string;
  tourStopId: string;
  scheduledDate: string;
  /** Optional for Monday bookings — use startTime instead for exact multi-slot times. */
  sessionIdentifier?: SessionIdentifier;
  /** Exact 24h start time for Monday multi-slot bookings (HH:MM:SS). Mutually exclusive with sessionIdentifier. */
  startTime?: string;
  /** @deprecated Use sessionIdentifier instead. Kept for stale-cart back-compat. */
  timeSlot?: string;
  studentCount: number | null;
  studentLevel: string | null;
  specialRequests: string | null;
  basePriceCents: number;
  travelSurchargeCents: number;
}

export interface CreateBookingInput {
  classes: BookingClassInput[];
  contactId: string;
  includeMarketingMaterials: boolean;
  groupTicketInquiry: boolean;
  termsVersion: string;
  termsAcceptedAt: string;
  notes?: string;
  couponCode?: string;
  startTimePreference?: 'early' | 'default' | 'late';
  simultaneousSessions?: boolean;
  simultaneousTrigger?: string | null;
}

export interface CreateBookingResponse {
  bookingId: string;
  status: 'pending';
  depositAmountCents: number;
  totalAmountCents: number;
  paymentIntentClientSecret: string;
  expiresAt: string; // 24-hour timeout
}

export interface PaymentIntent {
  id: string;
  clientSecret: string;
  amount: number;
  status: 'requires_payment_method' | 'requires_confirmation' | 'processing' | 'succeeded' | 'canceled';
}

export interface BookingConfirmation {
  id: string;
  status: 'confirmed';
  showName: string;
  showLogoUrl: string | null;
  venueName: string;
  venueCity: string;
  venueState: string;
  classes: {
    id: string;
    className: string;
    scheduledDate: string;
    /** Raw TIME value from DB (e.g. '10:00:00'). Render via formatTimeDisplay from @sda/shared. */
    startTime: string;
    studentCount: number | null;
    totalPriceCents: number;
  }[];
  subtotalCents: number;
  travelSurchargeCents: number;
  marketingFeeCents: number;
  discountCents: number;
  couponCode: string | null;
  totalAmountCents: number;
  depositPaidCents: number;
  balanceAmountCents: number;
  balanceDueDate: string | null;
  groupTicketInquiry: boolean;
  confirmationNumber: string;
  bookedAt: string;
}

export interface TermsVersion {
  version: string;
  content: string;
  effectiveDate: string;
}

// =============================================================================
// Auth Helper
// =============================================================================

/**
 * Get the auth token from localStorage
 * Must match the storage key used by AuthContext
 */
function getAuthToken(): string | null {
  try {
    const tokensJson = localStorage.getItem('sda_auth_tokens');
    if (!tokensJson) return null;
    const tokens = JSON.parse(tokensJson) as { access_token: string; expires_at: number };
    // Check if token is expired
    if (tokens.expires_at * 1000 < Date.now()) {
      return null;
    }
    return tokens.access_token;
  } catch {
    return null;
  }
}

async function authenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  handleUnauthorized(response);
  return response;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get the current terms and conditions
 */
export async function getCurrentTerms(): Promise<TermsVersion> {
  const response = await fetch(`${API_BASE_URL}/api/terms/current`);
  const result = await response.json();

  if (!response.ok) {
    throwApiError(response, result, 'Failed to get terms');
  }

  return result.data;
}

/**
 * Create a new booking with payment intent
 */
export async function createBooking(
  input: CreateBookingInput
): Promise<CreateBookingResponse> {
  const response = await authenticatedFetch(`${API_BASE_URL}/api/bookings`, {
    method: 'POST',
    // SDA-267: attach stored campaign attribution out-of-band; the server
    // validates and stamps it (absent/invalid never blocks the booking).
    body: JSON.stringify({ ...input, attribution: getAttribution() ?? undefined }),
  });

  const result = await response.json();

  if (!response.ok) {
    throwApiError(response, result, 'Failed to create booking');
  }

  return result.data;
}

/**
 * Confirm a booking after successful payment
 */
export async function confirmBooking(
  bookingId: string,
  paymentIntentId: string
): Promise<BookingConfirmation> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/bookings/${bookingId}/confirm`,
    {
      method: 'POST',
      body: JSON.stringify({ paymentIntentId }),
    }
  );

  const result = await response.json();

  if (!response.ok) {
    throwApiError(response, result, 'Failed to confirm booking');
  }

  return result.data;
}

/**
 * Get booking confirmation details
 */
export async function getBookingConfirmation(
  bookingId: string
): Promise<BookingConfirmation> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/bookings/${bookingId}/confirmation`
  );

  const result = await response.json();

  if (!response.ok) {
    throwApiError(response, result, 'Failed to get confirmation');
  }

  return result.data;
}

/**
 * Cancel a pending booking
 */
export async function cancelBooking(bookingId: string): Promise<void> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/bookings/${bookingId}/cancel`,
    {
      method: 'POST',
    }
  );

  if (!response.ok) {
    const result = await response.json();
    throwApiError(response, result, 'Failed to cancel booking');
  }
}

/**
 * Get Stripe publishable key
 */
export async function getStripePublishableKey(): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/api/payments/config`);
  const result = await response.json();

  if (!response.ok) {
    throwApiError(response, result, 'Failed to get payment config');
  }

  return result.data.publishableKey;
}

/**
 * Validate a coupon code for a studio
 */
export interface ValidateCouponResponse {
  couponId: string;
  code: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  appliesTo: 'total' | 'sessions_only' | 'addons_only';
}

export async function validateCoupon(code: string, studioId: string): Promise<ValidateCouponResponse> {
  const response = await authenticatedFetch(`${API_BASE_URL}/api/bookings/validate-coupon`, {
    method: 'POST',
    body: JSON.stringify({ code, studioId }),
  });

  const result = await response.json();

  if (!response.ok) {
    throwApiError(response, result, 'Failed to validate coupon');
  }

  return result.data;
}

// =============================================================================
// Utilities
// =============================================================================

/**
 * Format price in cents to display string
 */
export function formatPrice(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

/**
 * Calculate days until balance due
 */
export function daysUntilDue(dueDateString: string): number {
  const dateStr = dueDateString.includes('T') ? dueDateString : dueDateString + 'T12:00:00';
  const dueDate = new Date(dateStr);
  const now = new Date();
  const diffTime = dueDate.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Format balance due date
 */
export function formatBalanceDueDate(dueDateString: string): string {
  const date = new Date(dueDateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}
